"use client";

import { useEffect, useState } from "react";
import Modal from "~/components/v3/themed/Modal";
import {
    FaLink,
    FaXTwitter,
    FaInstagram,
    FaFacebook,
    FaLinkedin,
} from "react-icons/fa6";
import { useEventContext } from "~/app/providers";
import { useShareFunctions } from "./useShareFunctions";
import { Card } from "~/components/v4/shadcn/Card";
import { Avatar, AvatarImage } from "~/components/v4/shadcn/Avatar";
import { getEventImageUrl } from "~/utils/images";
import { getCommunityImageUrl } from "~/components/v4/ui-helpers/community";
import { DateTime } from "luxon";
import RiverStar from "~/components/v4/icons/RiverStar";
import { motion } from "framer-motion";
import { GetEventFromSlug } from "~/app/beta/events/[eventSlug]/actions";
import { Sheet, SheetContent } from "~/components/v3/shadcn/Sheet";
import { useIsMobileV2 } from "~/hooks/useIsMobile";
import { useQRCode } from "next-qrcode";
import { APP_URL } from "~/utils/constants";
import { IoCopyOutline } from "react-icons/io5";
import { cn } from "~/utils/fontsConfig";
import { getWhitelabeledCommunityUrl } from "~/server/api/helpers/communityHelpers";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { CustomizableLayout } from "~/types/CustomizableLayout";

const ModalOrSheet = ({
    open,
    onOpenChange,
    title,
    children,
    type,
    customization,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    children: React.ReactNode;
    type: "modal" | "sheet";
    customization?: CustomizableLayout;
}) => {
    useEffect(() => {
        if (
            type === "sheet" &&
            typeof window !== "undefined" &&
            window.Intercom
        ) {
            window.Intercom("update", { hide_default_launcher: open });
        }
    }, [open, type]);
    if (type === "sheet")
        return (
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent
                    side="bottom"
                    showCloseIcon={true}
                    className={cn("max-h-[90dvh]", customization?.className)}
                >
                    <div className={cn("p-4")}>{children}</div>
                </SheetContent>
            </Sheet>
        );
    return (
        <Modal
            title={title}
            open={open}
            onOpenChange={onOpenChange}
            className={cn("max-h-[90dvh]")}
            customization={customization}
        >
            {children}
        </Modal>
    );
};

type ShareComponentProps = {
    isVisible: boolean;
    setIsVisible: (show: boolean) => void;
    event: GetEventFromSlug;
    useUserUploadedImage?: boolean;
    customization?: CustomizableLayout;
};

const ShareEventModal = ({
    isVisible,
    setIsVisible,
    event,
    useUserUploadedImage = false,
    customization,
}: ShareComponentProps) => {
    const isMobile = useIsMobileV2();
    const [copiedLink, setCopiedLink] = useState(false);

    // Hide intercom button when modal is open
    useEffect(() => {
        if (typeof window !== "undefined" && window.Intercom) {
            if (isVisible) {
                window.Intercom("update", { hide_default_launcher: true });
            } else {
                window.Intercom("update", { hide_default_launcher: false });
            }
        }
    }, [isVisible]);

    const {
        shareLinks,
        copyToClipboardWithToast,
        shareToX,
        shareToInstagram,
        shareToFacebook,
        shareToLinkedIn,
    } = useShareFunctions(event.id, useUserUploadedImage);

    const copyLink = async () => {
        if (!shareLinks?.eventUrl) throw new Error("Event URL not found");
        copyToClipboardWithToast(shareLinks.eventUrl, "Event");
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
    };
    const appUrl =
        getWhitelabeledCommunityUrl(event.community?.slug) ?? APP_URL;

    const isProfileEvent = !!event?.user;
    const host = event.hosts?.[0];
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const shareOptions = [
        {
            name: "Copy Link",
            icon: IoCopyOutline,
            iconColor: "#000",
            backgroundColor: "#E2FF05",
            action: copyLink,
        },
        {
            name: "X",
            icon: FaXTwitter,
            iconColor: "#fff",
            backgroundColor: "#000000",
            action: shareToX,
        },
        {
            name: "LinkedIn",
            icon: FaLinkedin,
            iconColor: "#fff",
            backgroundColor: "#0A66C2",
            action: shareToLinkedIn,
        },
        {
            name: "Instagram",
            icon: FaInstagram,
            iconColor: "#fff",
            background:
                "linear-gradient(145deg, #f09433 0%, #e6683c 25%, #e6683c 27%, #dc2743 40%, #dc2743 50%, #dc2743 55%, #bc1888 63%, #bc1888 100%)",
            action: shareToInstagram,
        },
        {
            name: "Facebook",
            icon: FaFacebook,
            iconColor: "#fff",
            backgroundColor: "#1877F2",
            action: shareToFacebook,
        },
    ];

    return (
        <ModalOrSheet
            title="Share"
            open={isVisible}
            onOpenChange={setIsVisible}
            type={isMobile ? "sheet" : "modal"}
            customization={customization}
        >
            <div
                className="grid"
                style={{
                    maxHeight: "inherit",
                    gridTemplateRows: "1fr minmax(0, min-content)",
                }}
            >
                <div className="overflow-hidden pb-2">
                    <div>
                        <div className="flex items-center gap-2">
                            {event.community ? (
                                <CommunityAvatar
                                    community={event.community}
                                    className="h-14 w-14 rounded-sm"
                                    height={24}
                                    width={24}
                                    customization={customization}
                                />
                            ) : (
                                event.user && (
                                    <UserAvatar
                                        user={event.user}
                                        className="h-14 w-14 rounded-sm"
                                        height={24}
                                        width={24}
                                        customization={customization}
                                    />
                                )
                            )}

                            <div className="flex flex-col overflow-hidden">
                                <div
                                    className={cn(
                                        "text-lg font-medium",
                                        customization?.labelClassName,
                                    )}
                                >
                                    {event.name}
                                </div>
                                <div
                                    className={cn(
                                        "truncate text-base text-v3-N300",
                                        customization?.descriptionClassName,
                                    )}
                                >
                                    rvr.to/events/{event.slug}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Preview section */}
                    <div
                        className={cn(
                            "mt-4 flex gap-4 rounded-lg border border-v3-N500 bg-v3-N600 p-4",
                            customization?.inputClassName,
                        )}
                    >
                        <div
                            className="relative aspect-video h-24 w-32 flex-shrink rounded-sm bg-cover bg-center"
                            style={{
                                backgroundImage: `url(${shareLinks?.userImageUrl ?? getEventImageUrl(event)})`,
                                transform: "translateZ(0)",
                            }}
                        />

                        <div className="line-clamp-4">
                            {shareLinks?.x?.text}
                        </div>
                    </div>
                </div>

                {/* Share options */}
                <div className="text-md px-5 py-3 font-medium">Share to</div>
                <div className="mb-4 mt-4 grid shrink-0 grid-cols-5 gap-x-3 gap-y-6 px-3">
                    {shareOptions.map((option) => (
                        <div
                            key={option.name}
                            className="flex flex-col items-center"
                        >
                            <div className="flex flex-col items-center gap-2">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => option.action()}
                                    className="flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all duration-200"
                                    style={{
                                        ...(option.backgroundColor && {
                                            backgroundColor:
                                                option.backgroundColor,
                                        }),
                                        ...(option.background && {
                                            background: option.background,
                                        }),
                                    }}
                                >
                                    <option.icon
                                        size={22}
                                        color={option.iconColor}
                                    />
                                </motion.button>
                                <span className="text-sm text-v3-N300">
                                    {option.name}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Copied notification */}
                {copiedLink ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-4 rounded-lg bg-v3-C400/10 p-2 text-center text-sm font-medium text-v3-C400"
                    >
                        Link copied to clipboard!
                    </motion.div>
                ) : (
                    <span className="block h-10 w-full"></span>
                )}
            </div>
        </ModalOrSheet>
    );
};

export default ShareEventModal;
